import React, { useState } from 'react';
import { fundraisingService } from '../../services/db';
import { useAuth } from '../../hooks/useAuth';
import { OFFICIAL_EMAILS } from '../../constants';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import SEO from '../../components/common/SEO';
import {
  HeartIcon,
  ShieldIcon,
  ChurchIcon,
  BibleIcon,
  LightningIcon,
  TrophyIcon,
  BriefcaseIcon,
  GlobeLanguageIcon,
  CheckIcon,
  CheckCircleIcon,
  InfoIcon,
} from '../../components/common/Icons';

const CAUSES = [
  { value: 'all', label: 'Where Needed Most (App Infrastructure, Engineers, Prizes & Outreach)' },
  { value: 'server_infrastructure', label: '1. Live Quiz Cloud Infrastructure (~$1/quiz room)' },
  { value: 'student_prizes', label: '2. Student Prizes, Trophies & Study Bibles' },
  { value: 'full_time_developers', label: '3. Full-Time Engineers & Developer Support' },
  { value: 'marketing_outreach', label: '4. Kingdom Outreach & Church League Partnerships' },
];

const FREQUENCIES = [
  { value: 'monthly',  label: 'Monthly Mission Partner (Sustain Ongoing Growth)' },
  { value: 'annual',   label: 'League & Trophy Sponsor (Annual Kingdom Impact)' },
  { value: 'one_time', label: 'Seed Sower (One-Time Catalyst Gift)' },
];

const CONTACT_METHODS = [
  { value: 'email_whatsapp', label: 'Email & WhatsApp' },
  { value: 'whatsapp',       label: 'WhatsApp Message' },
  { value: 'email',          label: 'Email Only' },
  { value: 'phone',          label: 'Phone Call' },
];

const Fundraising = () => {
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    donorName: user?.displayName || '',
    email: user?.email || '',
    phone: '',
    frequency: 'monthly',
    cause: 'all',
    preferredContact: 'email_whatsapp',
    prayerNote: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pledgeReceipt, setPledgeReceipt] = useState(null);
  const [formError, setFormError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError('');
  };

  const handlePledgeSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.donorName.trim() || !formData.email.trim()) {
      setFormError('Please enter your name and email address.');
      return;
    }

    setIsSubmitting(true);
    try {
      const receipt = await fundraisingService.submitPledge({
        ...formData,
        userId: user?.uid || '',
      });
      setPledgeReceipt(receipt);
    } catch (err) {
      console.error('Failed to submit partnership pledge:', err);
      const errMsg = err?.message?.includes('permission')
        ? 'Database write permission issue. Please ensure Firestore rules allow public pledge submissions.'
        : 'Failed to register partnership interest. Please try again or reach out to us directly.';
      setFormError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fundraising-page">
      <SEO
        title="Partner with Us & Support Bible Contests — Kingdom Sponsorship"
        description="Support Bible Contest in building Scripture habits for 100,000+ young believers. Sponsor live quiz infrastructure, youth prizes, and Study Bibles."
        keywords="Christian fundraising, Bible contest sponsorship, sponsor student Bible quiz, Christian ministry donation, youth ministry support"
        canonicalUrl="/donate"
        schema={{
          "@context": "https://schema.org",
          "@type": "DonateAction",
          "name": "Partner with Bible Contest Ministry",
          "description": "Sponsor live Scripture quiz cloud infrastructure, student trophies, and Study Bibles.",
          "recipient": {
            "@type": "Organization",
            "name": "Bible Contest Ministry",
            "url": "https://biblecontest.app/"
          }
        }}
      />

      {/* Hero Header */}
      <section className="fundraising-hero">
        <span className="hero-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <HeartIcon size={14} style={{ color: 'var(--clr-primary)' }} />
          <span>SOW INTO ETERNITY</span>
        </span>
        <h1 className="fundraising-hero__title">
          Help Us Put God's Word into the Hearts of 100,000+ Young Minds
        </h1>
        <p className="fundraising-hero__subtitle">
          In a digital age full of distractions, we build gamified, ad-free Bible leagues that turn daily Scripture into a habit youth love. Partner with us to power the technology, crown young winners, and keep God's Word free for every church.
        </p>

        {/* Accountability & Stewardship Promise */}
        <div className="accountability-banner">
          <div className="accountability-content">
            <div className="accountability-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <ShieldIcon size={14} style={{ color: '#059669' }} />
              <span>Financial Transparency &amp; Kingdom Stewardship</span>
            </div>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ChurchIcon size={18} style={{ color: 'var(--clr-primary)', flexShrink: 0 }} />
              <span>Our Stewardship Commitment</span>
            </h4>
            <p>
              <strong>We use every penny responsibly and transparently.</strong> Your partnership directly powers high-speed live quiz servers (~$1/quiz), funds tangible rewards like Study Bibles and trophies, sustains dedicated software engineers, and expands Gospel outreach to Sunday schools worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Four Pillars Section */}
      <section className="pillars-section">
        <h2 className="section-heading">How Your Partnership Makes an Eternal Impact</h2>
        <div className="pillars-grid">
          {/* Pillar 1 */}
          <div className="pillar-card pillar-card--amber">
            <div className="pillar-card-topbar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="pillar-card__icon pillar-card__icon--amber">
                  <LightningIcon size={22} />
                </div>
                <span className="pillar-number-badge">PILLAR 01</span>
              </div>
              <span className="pillar-metric-badge amber">~$1 / Quiz Room</span>
            </div>
            <h3>Keep Scripture Free &amp; Live for Every Child</h3>
            <p>
              Running real-time live contests for thousands of believers requires continuous cloud servers and instant sync. <strong>Just $1 powers an entire live interactive quiz room for 50+ students with zero ads and zero lag.</strong>
            </p>
            <ul className="pillar-checklist">
              <li>
                <CheckIcon size={14} style={{ color: '#D97706', flexShrink: 0, marginTop: '2px' }} />
                <span>Covers cloud database sync for every live quiz played (~$1/quiz room)</span>
              </li>
              <li>
                <CheckIcon size={14} style={{ color: '#D97706', flexShrink: 0, marginTop: '2px' }} />
                <span>High-speed servers ensuring zero latency during church and youth leagues</span>
              </li>
              <li>
                <CheckIcon size={14} style={{ color: '#D97706', flexShrink: 0, marginTop: '2px' }} />
                <span>Guarantees the platform remains 100% free, safe, and ad-free worldwide</span>
              </li>
            </ul>
          </div>

          {/* Pillar 2 */}
          <div className="pillar-card pillar-card--indigo">
            <div className="pillar-card-topbar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="pillar-card__icon pillar-card__icon--indigo">
                  <TrophyIcon size={22} />
                </div>
                <span className="pillar-number-badge">PILLAR 02</span>
              </div>
              <span className="pillar-metric-badge indigo">Youth Rewards</span>
            </div>
            <h3>Crown Young Scholars &amp; Memory Champions</h3>
            <p>
              Rewarding dedication with real Study Bibles, trophies, and awards that inspire children, youth, and Sunday school students to build lifelong Scripture memory habits.
            </p>
            <ul className="pillar-checklist">
              <li>
                <CheckIcon size={14} style={{ color: '#6366F1', flexShrink: 0, marginTop: '2px' }} />
                <span>Monthly &amp; seasonal contest prize pools for student leagues</span>
              </li>
              <li>
                <CheckIcon size={14} style={{ color: '#6366F1', flexShrink: 0, marginTop: '2px' }} />
                <span>Physical Study Bibles and Christian literature sent directly to winners</span>
              </li>
              <li>
                <CheckIcon size={14} style={{ color: '#6366F1', flexShrink: 0, marginTop: '2px' }} />
                <span>Incentivizing youth to fall in love with God's Word early in life</span>
              </li>
            </ul>
          </div>

          {/* Pillar 3 */}
          <div className="pillar-card pillar-card--emerald">
            <div className="pillar-card-topbar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="pillar-card__icon pillar-card__icon--emerald">
                  <BriefcaseIcon size={22} />
                </div>
                <span className="pillar-number-badge">PILLAR 03</span>
              </div>
              <span className="pillar-metric-badge emerald">99.9% Uptime</span>
            </div>
            <h3>World-Class Christian Tech &amp; Dedicated Engineers</h3>
            <p>
              Sustaining full-time software developers, UI designers, and theological reviewers who work daily to innovate, maintain 99.9% uptime, and craft world-class spiritual tools.
            </p>
            <ul className="pillar-checklist">
              <li>
                <CheckIcon size={14} style={{ color: '#059669', flexShrink: 0, marginTop: '2px' }} />
                <span>Full-time developers building next-gen interactive features with zero downtime</span>
              </li>
              <li>
                <CheckIcon size={14} style={{ color: '#059669', flexShrink: 0, marginTop: '2px' }} />
                <span>Theological reviewers verifying biblical accuracy across every quiz question</span>
              </li>
              <li>
                <CheckIcon size={14} style={{ color: '#059669', flexShrink: 0, marginTop: '2px' }} />
                <span>Dedicated onboarding and technical assistance for church partners</span>
              </li>
            </ul>
          </div>

          {/* Pillar 4 */}
          <div className="pillar-card pillar-card--rose">
            <div className="pillar-card-topbar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="pillar-card__icon pillar-card__icon--rose">
                  <GlobeLanguageIcon size={22} />
                </div>
                <span className="pillar-number-badge">PILLAR 04</span>
              </div>
              <span className="pillar-metric-badge rose">Global Outreach</span>
            </div>
            <h3>Ignite Bible Leagues Across Churches &amp; Nations</h3>
            <p>
              Equipping Sunday schools, Christian academies, youth ministries, and families worldwide to spark a passion for Scripture among the next generation.
            </p>
            <ul className="pillar-checklist">
              <li>
                <CheckIcon size={14} style={{ color: '#E11D48', flexShrink: 0, marginTop: '2px' }} />
                <span>Youth pastor outreach kits and church league partnership programs</span>
              </li>
              <li>
                <CheckIcon size={14} style={{ color: '#E11D48', flexShrink: 0, marginTop: '2px' }} />
                <span>Multi-language expansion bringing regional Bible leagues to global communities</span>
              </li>
              <li>
                <CheckIcon size={14} style={{ color: '#E11D48', flexShrink: 0, marginTop: '2px' }} />
                <span>Digital campaigns inspiring consistent daily Bible study habits</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Contribution & Pledge Form Section */}
      <section className="donation-form-section">
        <div className="card donation-card">
          {pledgeReceipt ? (
            <div className="pledge-confirmation">
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', background: 'var(--clr-primary-subtle)', color: 'var(--clr-primary)', margin: '0 auto 16px', border: '1px solid var(--clr-primary-light)' }}>
                <CheckCircleIcon size={34} />
              </div>
              <div className="pledge-confirmation-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <HeartIcon size={13} />
                <span>PLEDGE RECEIVED</span>
              </div>
              <h2>Thank You for Sowing into This Ministry!</h2>
              <p className="pledge-donor-line">
                Dear <strong>{formData.donorName}</strong>, your pledge to partner in{' '}
                <strong className="pledge-highlight">
                  {CAUSES.find((c) => c.value === formData.cause)?.label}
                </strong>{' '}
                makes an eternal difference in the lives of thousands.
              </p>

              <div className="pledge-receipt-box">
                <div className="receipt-row">
                  <span>Partnership Reference ID:</span>
                  <code>{pledgeReceipt.id}</code>
                </div>
                <div className="receipt-row">
                  <span>Registered Email:</span>
                  <strong>{formData.email}</strong>
                </div>
                {formData.phone && (
                  <div className="receipt-row">
                    <span>Phone / WhatsApp:</span>
                    <strong>{formData.phone}</strong>
                  </div>
                )}
                <div className="receipt-row">
                  <span>Partnership Cadence:</span>
                  <strong>{FREQUENCIES.find((f) => f.value === formData.frequency)?.label}</strong>
                </div>
              </div>

              <div style={{
                background: 'rgba(99, 102, 241, 0.08)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                borderRadius: '12px',
                padding: '20px',
                marginTop: '20px',
                textAlign: 'left'
              }}>
                <h4 style={{ color: 'var(--clr-primary)', marginBottom: '8px', fontSize: '15px' }}>
                  What Happens Next?
                </h4>
                <p style={{ fontSize: '13.5px', color: 'var(--clr-text-2)', lineHeight: 1.6, margin: 0 }}>
                  We have logged your partnership pledge into our system. Our ministry leadership team will personally connect with you via <strong>{formData.preferredContact === 'whatsapp' ? 'WhatsApp' : formData.preferredContact === 'phone' ? 'Phone' : 'Email/WhatsApp'}</strong> ({formData.phone || formData.email}) within 24–48 hours with direct transfer details and further guidance.
                </p>
              </div>

              <Button
                variant="primary"
                size="lg"
                onClick={() => setPledgeReceipt(null)}
                style={{ marginTop: '24px', width: '100%', minHeight: '48px' }}
              >
                Submit Another Inquiry
              </Button>
            </div>
          ) : (
            <form onSubmit={handlePledgeSubmit} className="giving-form">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', borderRadius: '10px', background: 'var(--clr-primary-subtle)', color: 'var(--clr-primary)', border: '1px solid var(--clr-primary-light)' }}>
                  <HeartIcon size={20} />
                </div>
                <h2 className="giving-form__title" style={{ margin: 0 }}>Join as a Kingdom Partner</h2>
              </div>
              <p className="giving-form__subtitle">
                Express your pledge to co-build this ministry or sponsor our Bible leagues.
              </p>

              {/* Direct Process Notification */}
              <div className="giving-notice-box">
                <div className="giving-notice-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <InfoIcon size={14} />
                  <span>Direct Partnership Process</span>
                </div>
                <p>
                  To maximize stewardship and avoid high payment gateway fees, submit your pledge below or reach out directly to our leadership team at{' '}
                  <a href={`mailto:${OFFICIAL_EMAILS.manager}`}>
                    {OFFICIAL_EMAILS.manager}
                  </a>
                  {' '}or{' '}
                  <a href={`mailto:${OFFICIAL_EMAILS.ceo}`}>
                    {OFFICIAL_EMAILS.ceo}
                  </a>
                  . Our team will connect with you directly with direct bank/UPI transfer instructions.
                </p>
              </div>

              {/* Form Error Banner */}
              {formError && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(239, 68, 68, 0.12)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#f87171',
                  fontSize: '13px',
                  lineHeight: '1.4',
                  marginBottom: '16px',
                }}>
                  {formError}
                </div>
              )}

              {/* Giving Frequency */}
              <div>
                <Select
                  label="Partnership Cadence"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                  options={FREQUENCIES}
                />
              </div>

              {/* Cause Selection */}
              <div style={{ marginTop: '16px' }}>
                <Select
                  label="Direct My Kingdom Partnership To"
                  name="cause"
                  value={formData.cause}
                  onChange={handleInputChange}
                  options={CAUSES}
                />
              </div>

              {/* Donor Information */}
              <div className="form-grid-2" style={{ marginTop: '16px' }}>
                <Input
                  label="Your Full Name"
                  name="donorName"
                  value={formData.donorName}
                  onChange={handleInputChange}
                  placeholder="e.g. Rachel Adams"
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="rachel@example.com"
                  required
                />
              </div>

              <div className="form-grid-2" style={{ marginTop: '16px' }}>
                <Input
                  label="Phone / WhatsApp Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="e.g. +1 555-019-2834"
                  required
                />
                <Select
                  label="Preferred Communication Channel"
                  name="preferredContact"
                  value={formData.preferredContact}
                  onChange={handleInputChange}
                  options={CONTACT_METHODS}
                />
              </div>

              {/* Optional Prayer Request / Dedication */}
              <div style={{ marginTop: '16px' }}>
                <Textarea
                  label="Optional Message or Prayer Request"
                  name="prayerNote"
                  value={formData.prayerNote}
                  onChange={handleInputChange}
                  placeholder="Share how God has worked in your life or any prayer request for our team..."
                  rows={2}
                />
              </div>

              <div style={{ marginTop: '24px' }}>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting}
                  icon={<HeartIcon size={16} />}
                  style={{ width: '100%', minHeight: '48px' }}
                >
                  {isSubmitting ? 'Recording Your Pledge...' : 'Become a Kingdom Partner →'}
                </Button>
                <p className="privacy-note">
                  All pledges are strictly confidential. Our team will reach out with direct guidance.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Scripture Reflection */}
      <section className="scripture-reflection-banner">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
          <BibleIcon size={26} style={{ color: 'var(--clr-secondary)' }} />
        </div>
        <blockquote>
          "Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver. And God is able to bless you abundantly..."
          <footer>— 2 Corinthians 9:7-8</footer>
        </blockquote>
      </section>
    </div>
  );
};

export default Fundraising;
